import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { createPipelineDefinitionsApi } from '../lib/pipelineDefinitionsApi';

// Requests in flight and definitions already read, one per (organization, pipeline), so every surface
// that needs the Sales definition (Kanban, reports, badges, campaign pickers) costs ONE request per
// session. The key carries the organization because signing out does not reload the page: the next
// user, possibly of another org, must never see the previous org's stages.
const requests = new Map();
const resolved = new Map();

const cacheKey = (orgId, pipelineId) => `${orgId || ''}:${pipelineId}`;

/** Drop the cached definition so the next read refetches. The admin Pipelines page calls this after a
 *  publish, and a board that is refused a stage calls it to learn the new list; with no argument it
 *  clears everything. It applies to every organization's entry for that pipeline id. */
export function invalidatePipelineDefinition(pipelineId) {
    if (pipelineId === undefined) {
        requests.clear();
        resolved.clear();
        return;
    }
    [requests, resolved].forEach((cache) => {
        [...cache.keys()].forEach((key) => { if (key.endsWith(`:${pipelineId}`)) cache.delete(key); });
    });
}

const initialState = (key, pipelineId) => (
    !pipelineId ? { key, definition: null, loading: false, error: null }
        : resolved.has(key) ? { key, definition: resolved.get(key), loading: false, error: null }
            : { key, definition: null, loading: true, error: null });

// Reads `GET /api/pipeline-definitions/{id}` (any org member may): the active stages, version and
// default stage. On error `definition` is null and `error` is set, and every caller falls back to its
// built-in defaults, so a surface is never blank. `reload()` refetches and keeps showing the current
// definition until the new one arrives.
export default function usePipelineDefinition(pipelineId) {
    const { getAuthHeader, user } = useAuth();
    const key = cacheKey(user?.org_id, pipelineId);
    const [reloads, setReloads] = useState(0);
    const [state, setState] = useState(() => initialState(key, pipelineId));

    useEffect(() => {
        if (!pipelineId) {
            setState({ key, definition: null, loading: false, error: null });
            return undefined;
        }
        if (resolved.has(key)) {
            setState({ key, definition: resolved.get(key), loading: false, error: null });
            return undefined;
        }
        let cancelled = false;
        // Same entry: keep what is shown while it reloads. Another entry (another org): start clean.
        setState((prev) => (prev.key === key ? (prev.loading ? prev : { ...prev, loading: true })
            : { key, definition: null, loading: true, error: null }));
        let request = requests.get(key);
        if (!request) {
            request = createPipelineDefinitionsApi(getAuthHeader).get(pipelineId);
            requests.set(key, request);
            request.then(
                (definition) => { resolved.set(key, definition); },
                // a failure is not cached: the next mount (or reload) tries again
                () => { if (requests.get(key) === request) requests.delete(key); },
            );
        }
        request.then(
            (definition) => { if (!cancelled) setState({ key, definition, loading: false, error: null }); },
            (error) => { if (!cancelled) setState({ key, definition: null, loading: false, error }); },
        );
        return () => { cancelled = true; };
    }, [key, pipelineId, getAuthHeader, reloads]);

    const reload = useCallback(() => {
        invalidatePipelineDefinition(pipelineId);
        setReloads((n) => n + 1);
    }, [pipelineId]);

    const current = state.key === key ? state : initialState(key, pipelineId);
    return { definition: current.definition, loading: current.loading, error: current.error, reload };
}
